/**
 * Server-side fetcher for the MerchandisingAssociation widget.
 *
 * Overrides the core fetcher purely to align its SWR cache key with the one
 * `getProductDetails` already uses. Core called `getProduct(cache, partNumber, context)`
 * without `marketId`/`stLocId`; because `getProductSWRKey` only adds those params when
 * truthy, that produced a *second* cache key for the same product and the same
 * `HCL_V2_findProductByPartNumber_Details` profile — so the PDP fetched the product
 * twice and serialized both copies into `__NEXT_DATA__`.
 *
 * The two responses were identical on every shared field; the store-scoped variant just
 * omits the chain-wide `inventories.<physicalStoreId>.quantity` fan-out (~208 entries)
 * that nothing on the storefront reads. `inventories.M<marketId>.quantity` — the only
 * inventory key consumed (getBundleComponents, MetadataScriptPDP) — is present and
 * identical in both.
 *
 * Passing the same params here collapses both fetchers onto one key, so
 * `getProductByKeyType` reuses the in-flight promise: one backend call, one payload.
 * The client hook already builds this key (`useProduct` reads marketId/stLocId from
 * `useDefaultStore`), so it is re-exported from core unchanged.
 */

import { getDefaultStore } from '@/data/Content/DefaultStore';
import { getProduct } from '@/data/Content/Product';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { ContentProps } from '@/data/types/ContentProps';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';

import { useMerchandisingAssociation } from '@/core/data/Content/MerchandisingAssociation';

const getMerchandisingAssociation = async ({ cache, id: partNumber, context }: ContentProps) => {
	// Mirrors getProductDetails exactly — any drift here reintroduces the duplicate key.
	const { physicalStoreId, uniqueId: stLocId } = await getDefaultStore(cache, context);
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	return getProduct(
		cache,
		partNumber.toString(),
		context,
		SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
		marketId,
		stLocId
	);
};

export { getMerchandisingAssociation, useMerchandisingAssociation };
